<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl mb-4 sm:mb-6 border-b-2 pb-3 sm:pb-4 px-4 sm:px-0">{{ __('Design Your Business Card') }}</h2>
            <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row w-full mx-auto card-properties gap-4 lg:gap-6 px-4 sm:px-6 lg:px-0">
                <div class="card-form w-full lg:w-1/2 mx-auto">
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                        <!-- Personal Information Section -->
                        <div class="mb-6 pb-6 border-b border-gray-200">
                            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <icon name="user" class="w-5 h-5 text-green-600" />
                                Personal Information
                            </h3>
                            <div class="space-y-4">
                                <div class="flex w-full">
                                    <text-input v-model="form.name" @keyup="changeTextAlign($event)" :error="form.errors.name" class="w-full pr-6" :label="__('Full Name')" type="text" autofocus autocapitalize="off" maxlength="100" />
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <text-input
                                        v-model="form.phone"
                                        :error="form.errors.phone || validationErrors.phone"
                                        @blur="validatePhoneField"
                                        class="w-full pr-6"
                                        :label="__('Phone')"
                                        type="tel"
                                        autofocus
                                        autocapitalize="off"
                                        maxlength="20"
                                        pattern="[\d\s\-\+\(\)\.]{10,}"
                                    />
                                    <text-input
                                        @keyup="changeFontSize($event)"
                                        v-model="form.email"
                                        :error="form.errors.email || validationErrors.email"
                                        @blur="validateEmailField"
                                        class="w-full pr-6"
                                        :label="__('Email')"
                                        type="email"
                                        autofocus
                                        autocapitalize="off"
                                        maxlength="255"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Company Information Section -->
                        <div class="mb-6 pb-6 border-b border-gray-200">
                            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <icon name="office" class="w-5 h-5 text-green-600" />
                                Company Information
                            </h3>
                            <div class="space-y-4">
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <text-input
                                        v-model="form.company"
                                        :error="form.errors.company"
                                        class="w-full pr-6"
                                        :label="__('Company Name')"
                                        type="text"
                                        autofocus
                                        autocapitalize="off"
                                        maxlength="100"
                                    />
                                    <text-input
                                        v-model="form.website"
                                        :error="form.errors.website || validationErrors.website"
                                        @blur="validateWebsiteField"
                                        class="w-full pr-6"
                                        :label="__('Website')"
                                        type="url"
                                        autofocus
                                        autocapitalize="off"
                                        placeholder="https://example.com"
                                        maxlength="255"
                                    />
                                </div>
                                <div class="flex w-full flex-col">
                                    <text-input v-model="form.address" :error="form.errors.address" class="w-full pr-6 pb-3" :label="__('Address Line 1')" type="text" autofocus autocapitalize="off" maxlength="200" />
                                    <text-input v-model="form.address_2" :error="form.errors.address_2" class="w-full pr-6" :label="__('Address Line 2')" type="text" autofocus autocapitalize="off" placeholder="City, State, ZIP" maxlength="200" />
                                </div>
                            </div>
                        </div>

                        <!-- Typography Section -->
                        <div class="mb-6 pb-6 border-b border-gray-200">
                            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <icon name="settings" class="w-5 h-5 text-green-600" />
                                Display Options
                            </h3>
                            <card-typography-controls
                                :model="form"
                                id-prefix="business"
                                :font-label="__('Card Name Font')"
                                :hint="__('Applies to your name on the business card preview.')"
                                :min-size="12"
                                :max-size="28"
                            />
                        </div>

                        <!-- Media Section -->
                        <div class="mb-4">
                            <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <icon name="camera" class="w-5 h-5 text-green-600" />
                                Images & Media
                            </h3>
                            <div class="space-y-4">
                                <!-- Company Logo Upload -->
                                <div class="flex w-full flex-col">
                                    <label class="block mb-2 text-sm font-medium text-gray-700" for="companyLogo">
                                        Company Logo
                                        <span class="text-xs text-gray-500 font-normal ml-1">(Recommended: 200x200px, PNG/JPG)</span>
                                    </label>
                                    <div class="relative">
                                        <input
                                            id="companyLogo"
                                            @change="companyLogoInputChange($event)"
                                            class="hidden"
                                            type="file"
                                            accept="image/*"
                                        >
                                        <label
                                            for="companyLogo"
                                            class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors group"
                                        >
                                            <div v-if="form.logo" class="flex flex-col items-center justify-center pt-2 pb-2">
                                                <img :src="form.logo" alt="Logo preview" class="h-16 w-16 object-contain mb-2 rounded" />
                                                <p class="text-sm text-gray-600">Click to change logo</p>
                                            </div>
                                            <div v-else class="flex flex-col items-center justify-center pt-2 pb-2">
                                                <icon name="camera" class="w-8 h-8 text-gray-400 group-hover:text-green-600 mb-2" />
                                                <p class="text-sm text-gray-600 group-hover:text-green-600">
                                                    <span class="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p class="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <!-- Background Image Upload -->
                                <div class="flex w-full flex-col">
                                    <label class="block mb-2 text-sm font-medium text-gray-700" for="backgroundImage">
                                        Background Image
                                        <span class="text-xs text-gray-500 font-normal ml-1">(Optional, Recommended: 1200x800px)</span>
                                    </label>
                                    <div class="relative">
                                        <input
                                            id="backgroundImage"
                                            @change="backgroundImageInputChange($event)"
                                            class="hidden"
                                            type="file"
                                            accept="image/*"
                                        >
                                        <label
                                            for="backgroundImage"
                                            class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors group"
                                        >
                                            <div v-if="form.background" class="flex flex-col items-center justify-center pt-2 pb-2 w-full h-full">
                                                <img :src="form.background" alt="Background preview" class="h-full w-full object-cover rounded mb-2" />
                                                <p class="text-sm text-gray-600">Click to change background</p>
                                            </div>
                                            <div v-else class="flex flex-col items-center justify-center pt-2 pb-2">
                                                <icon name="image" class="w-8 h-8 text-gray-400 group-hover:text-green-600 mb-2" />
                                                <p class="text-sm text-gray-600 group-hover:text-green-600">
                                                    <span class="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p class="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
              <div class="card-items flex w-full lg:w-1/2 mx-auto py-0">
                <div class="flex flex-wrap flex-col items-center w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 ps-card-tool" :style="cardTextCssVars">
                    <!-- Preview Header -->
                    <div class="w-full mb-4 pb-4 border-b border-gray-200">
                        <h3 class="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-2">
                            <icon name="aperture" class="w-5 h-5 text-green-600" />
                            Live Preview
                        </h3>
                        <p class="text-xs text-gray-500">See how your business card will look</p>
                    </div>
                    <div v-if="card_layout === 'horizontal'" class="card__entry">
                        <div class="flex flex-col card__content id_card horizontal">
                            <div v-if="form.background" class="id__card__bg_1" :style="{backgroundImage: `url(${form.background})`}"></div>
                            <div v-else class="id__card__bg_1"></div>
                            <div class="id__card__bg_2"></div>
                            <div class="main__content">
                                <div class="flex justify-between">
                                    <div class="flex flex-col w-[46%] photo_name">
                                        <div v-if="form.logo" class="flex qr_code">
                                            <img :src="form.logo" class="qr__image" />
                                        </div>
                                        <div ref="user__name" v-if="form.name" class="school_name ps-card-typed-text" :style="nameLayoutStyle">{{ form.name }}</div>
                                    </div>
                                    <div class="w-[54%] flex flex-col qr_code">
                                        <div class="flex ___desc">
                                            <div class="flex flex-col __desc__names flex-start items-start gap-1">
                                                <div class="flex pb-2 font-bold">{{ form.company }}</div>
                                                <div class="flex phone"> <icon name="phone" class="w-4 h-4 mr-2 icon" /> {{ form.phone }}</div>
                                                <div class="flex website"> <icon name="website" class="w-4 h-4 mr-2 icon" /> {{ form.website }}</div>
                                                <div class="flex address">
                                                    <div class="flex pt-1 mr-2">
                                                        <icon name="location" class="w-4 h-4 icon" />
                                                    </div>
                                                    <div class="flex flex-col pt-[2px]">
                                                        <span class="flex">{{ form.address }}</span>
                                                        <span class="flex">{{ form.address_2 }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex email e_txt" ref="user__email">{{ form.email }}</div>
                            </div>
                            <div class="w-full flex justify-between items-center bottom__content">
                                <div class="flex w-full bar"></div>
                            </div>
                        </div>
                    </div>
                    <div v-if="card_layout === 'vertical'" class="card__entry">
                        <div class="flex flex-col justify-between card__content id_card vertical">
                            <div v-if="form.background" class="id__card__bg_1" :style="{backgroundImage: `url(${form.background})`}"></div>
                            <div v-else class="id__card__bg_1"></div>
                            <div class="id__card__bg_2"></div>
                            <div class="flex justify-between flex-col main__content pt-3">
                                <div class="flex flex-col items-center w-full photo_name">
                                    <div v-if="form.logo" class="flex justify-center qr_code">
                                        <img :src="form.logo" class="qr__image" />
                                    </div>
                                    <div v-if="form.name" class="school_name ps-card-typed-text">{{ form.name }}</div>
                                    <div ref="email_input" class="flex email">{{ form.email }}</div>
                                </div>
                                <div class="w-full flex flex-col qr_code">
                                    <div class="flex ___desc justify-center">
                                        <div class="flex flex-col __desc__names justify-center items-center gap-1">
                                            <div class="flex pt-5 w-full justify-center font-bold">{{ form.company }}</div>
                                            <div class="flex phone"> <icon name="phone" class="w-4 h-4 mr-2 icon" /> {{ form.phone }}</div>
                                            <div class="flex website"> <icon name="website" class="w-4 h-4 mr-2 icon" /> {{ form.website }}</div>
                                            <div class="flex address">
                                                <div class="flex pt-1 mr-2">
                                                    <icon name="location" class="w-4 h-4 icon" />
                                                </div>
                                                <div class="flex flex-col pt-[2px]">
                                                    <span class="flex">{{ form.address }}</span>
                                                    <span class="flex">{{ form.address_2 }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="w-full flex justify-between items-center gap-[8%] bottom__content">
                                <div class="flex w-full bar"></div>
                            </div>
                        </div>
                    </div>
                    <!-- Layout Selection -->
                    <div class="w-full mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-2">Card Layout</label>
                        <div class="grid grid-cols-2 gap-3">
                            <button
                                @click="()=>{card_layout = 'horizontal'}"
                                :class="[
                                    'px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium flex flex-col items-center gap-2',
                                    card_layout === 'horizontal'
                                        ? 'border-green-600 bg-green-50 text-green-700 shadow-sm'
                                        : 'border-gray-300 hover:border-green-400 text-gray-700 bg-white'
                                ]"
                            >
                                <icon name="arrow-right" class="w-5 h-5 rotate-90" />
                                Horizontal
                            </button>
                            <button
                                @click="()=>{card_layout = 'vertical'}"
                                :class="[
                                    'px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium flex flex-col items-center gap-2',
                                    card_layout === 'vertical'
                                        ? 'border-green-600 bg-green-50 text-green-700 shadow-sm'
                                        : 'border-gray-300 hover:border-green-400 text-gray-700 bg-white'
                                ]"
                            >
                                <icon name="arrow-right" class="w-5 h-5" />
                                Vertical
                            </button>
                        </div>
                    </div>
                    <!-- Action Buttons -->
                    <div class="w-full mt-6 pt-4 border-t border-gray-200">
                        <div class="flex flex-col gap-3">
                            <button
                                @click="downloadAsPNG()"
                                :disabled="loading"
                                class="w-full text-white flex justify-center items-center bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 focus:outline-none rounded-lg px-6 py-3 gap-2 font-semibold transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transform hover:-translate-y-0.5"
                            >
                                <icon v-if="!loading" name="download" class="w-5 h-5 text-white fill-white" />
                                <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {{ loading ? 'Generating...' : 'Download Image' }}
                            </button>
                            <button
                                @click="printCard()"
                                class="w-full text-white flex justify-center items-center bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 active:from-purple-800 active:to-purple-900 focus:outline-none rounded-lg px-6 py-3 gap-2 font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                                <icon name="print" class="w-5 h-5 text-white fill-white" />
                                <span class="text-white">Print Business Card</span>
                            </button>
                        </div>
                        <p class="text-xs text-gray-500 text-center mt-3">
                            Download your card as a high-quality PNG image
                        </p>
                    </div>
                </div>
              </div>
            </div>
    </div>
    <!-- Loading Process -->
    <div v-if="form.processing" class="processing-overlay">
      <div class="background" />
      <div class="loader">
        <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" style="background: none;">
          <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.875s" />
          </circle>
          <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.75s" />
          </circle>
          <circle cx="50" cy="75" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.625s" />
          </circle>
          <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.5s" />
          </circle>
          <circle cx="25" cy="50" fill="#ffffff" r="4.8">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.375s" />
          </circle>
          <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.25s" />
          </circle>
          <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="-0.125s" />
          </circle>
          <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
            <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s" repeatCount="indefinite" begin="0s" />
          </circle>
        </svg>
      </div>
    </div>
    <!-- Loading Process -->
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import {toPng } from 'html-to-image';
import TextareaInput from '../../Shared/TextareaInput.vue'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'
import CardTypographyControls from '../../Shared/CardTypographyControls.vue'
import { validateEmail, validateURL, validatePhone, validateFileSize, validateFileType, normalizeURL } from '../../utils/validation.js'
import { DEFAULT_BUSINESS_CARD_TYPOGRAPHY, buildCardTextCssVars } from '../../utils/cardFontOptions.js'

export default {
    components: {
        SidebarNavigation,
        CardTypographyControls,
        TextareaInput,
        Icon,
        TextInput,
    },
    layout: Layout,
    props: {
        title: String,
        uri: {
            type: String,
            required: false
        },
        defaultCardLogo: {
            type: String,
            default: '/images/card/no-image.png'
        },
        defaultCardBackground: {
            type: String,
            default: '/images/card/card-bg.jpg'
        },
    },
    remember: 'form',
    data() {
        return {
            loading: false,
            step: 3,
            file_accept: '.csv,.xlsx,.tsv,.ods,.xls,.slk,.xml',
            validationErrors: {},
            form: this.$inertia.form({
                name: '',
                title: '',
                phone: '',
                email: '',
                company: '',
                website: '',
                address: '',
                address_2: '',
                cards: [],
                logo: null,
                background: null,
                background_ref: null,
                qr_code: null,
                promotional_text: '',
                website_url: '',
                disclaimer: '',
                font_family: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_family,
                font_size: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_size,
                font_bold: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_bold,
                font_italic: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_italic,
                font_color: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_color,
                text_shadow: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow,
                text_shadow_blur: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow_blur,
            }),
            enable_csv_import: false,
            cardPages: [],
            print_preview: false,
            allPortraitCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14],
            allLandscapeCardDesigns: [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],
            cardPortraitDesigns: [],
            cardLandscapeDesigns: [],
            page_number_portrait: 1,
            page_number_landscape: 1,
            selected_design: { number: 1, type: 'portrait' },
            current_card: null,
            paper: {
                size: 'a4',
            },
            card_layout: 'horizontal',
            referral_layout: 'horizontal',
            timeout_qr: null,
            refreshed_user_at: (new Date()).valueOf(),
        }
    },
    computed: {
        cardTextCssVars() {
            return buildCardTextCssVars({
                font_family: this.form.font_family ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_family,
                font_size: this.form.font_size ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_size,
                font_bold: this.form.font_bold ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_bold,
                font_italic: this.form.font_italic ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_italic,
                font_color: this.form.font_color ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_color,
                text_shadow: this.form.text_shadow ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow,
                text_shadow_blur: this.form.text_shadow_blur ?? DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow_blur,
            })
        },
        nameLayoutStyle() {
            if (this.card_layout !== 'horizontal' || !this.form.name) {
                return {}
            }

            if (this.form.name.length > 14) {
                return {
                    textAlign: 'center',
                    width: '120px',
                }
            }

            return {
                textAlign: 'left',
                width: 'auto',
            }
        },
    },
    created() {
        this.ensureTypographyFields()
        this.setForm({
            name: 'Your Name',
            phone: '833-123-4567',
            email: 'your-email@mail.com',
            company: 'ABC Auto Sales',
            website: 'abc-auto-sales.com',
            address: '123 Main St',
            address_2: 'Houston, TX 77053',
            logo: this.defaultCardLogo,
            background: this.defaultCardBackground,
            qr_code: '/images/card/qr.svg',
            promotional_text: 'Refer a friend or family member, and you both get rewarded! It\'s a win-win for you and your friend!',
            website_url: 'refer.direct/demo',
            disclaimer: 'Must submit a referral form prior to visiting the dealership. Available to new clients only. Additional Terms and Conditions apply. Please see the referral program website for complete details.',
            font_family: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_family,
            font_size: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_size,
            font_bold: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_bold,
            font_italic: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_italic,
            font_color: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.font_color,
            text_shadow: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow,
            text_shadow_blur: DEFAULT_BUSINESS_CARD_TYPOGRAPHY.text_shadow_blur,
        })
    },
    mounted() {
        this.syncEmailPadding()
    },
    watch: {
        'form.name'() {
            this.syncEmailPadding()
        },
        card_layout() {
            this.syncEmailPadding()
        },
    },
    methods: {
        ensureTypographyFields() {
            Object.entries(DEFAULT_BUSINESS_CARD_TYPOGRAPHY).forEach(([key, value]) => {
                if (this.form[key] === undefined || this.form[key] === null) {
                    this.form[key] = value
                }
            })
        },
        validateEmailField() {
            if (this.form.email) {
                const result = validateEmail(this.form.email)
                if (!result.valid) {
                    this.validationErrors.email = result.message
                } else {
                    delete this.validationErrors.email
                }
            } else {
                delete this.validationErrors.email
            }
        },
        validatePhoneField() {
            if (this.form.phone) {
                const result = validatePhone(this.form.phone)
                if (!result.valid) {
                    this.validationErrors.phone = result.message
                } else {
                    delete this.validationErrors.phone
                }
            } else {
                delete this.validationErrors.phone
            }
        },
        validateWebsiteField() {
            if (this.form.website) {
                const result = validateURL(this.form.website)
                if (!result.valid) {
                    this.validationErrors.website = result.message
                } else {
                    delete this.validationErrors.website
                    // Normalize URL
                    if (result.normalized) {
                        this.form.website = result.normalized
                    }
                }
            } else {
                delete this.validationErrors.website
            }
        },
        validateFileUpload(file, type = 'image') {
            if (!file) return { valid: true }

            const sizeResult = validateFileSize(file, 5)
            if (!sizeResult.valid) {
                if (window.$toast) {
                    window.$toast.error(sizeResult.message)
                }
                return sizeResult
            }

            if (type === 'image') {
                const typeResult = validateFileType(file)
                if (!typeResult.valid) {
                    if (window.$toast) {
                        window.$toast.error(typeResult.message)
                    }
                    return typeResult
                }
            }

            return { valid: true }
        },
        generateQrWhileStop(){
            clearTimeout(this.timeout_qr)
            const self = this
            this.timeout_qr = setTimeout(function () {
                self.generateWebsiteQR()
            }, 2000)
        },
        clearInput(){
            clearTimeout(this.timeout_qr)
        },
        generateWebsiteQR(){
            if(this.form.website_url.length){
                const text = (this.form.website_url.indexOf('://') === -1) ? 'https://' + this.form.website_url : this.form.website_url;
                this.axios.post(this.route('generate.qr'), { text }).then((response) => {
                    if( response.data && response.data.image ){
                        this.form.qr_code = response.data.image;
                    }
                }).catch((error) => {
                    console.log(error)
                    this.form.processing = false
                })
            }
        },
        changeTextAlign() {
            this.syncEmailPadding()
        },
        syncEmailPadding() {
            this.$nextTick(() => {
                if (this.card_layout === 'horizontal' && this.$refs.user__email && this.$refs.user__name) {
                    this.$refs.user__email.style.paddingLeft = this.$refs.user__name.offsetLeft
                        ? `${this.$refs.user__name.offsetLeft - 27}px`
                        : '29px'
                }
            })
        },
        changeFontSize(e){
            if(this.card_layout === 'vertical'){
                const length = e.currentTarget.value.length
                // this.$refs.email_input.style.fontSize = length > 27 ? 27/length * 12 + 'px' : '12px'
            }
        },
        downloadAsPNG(referral){
            this.loading = true
            const target_selector = document.getElementsByClassName(referral ? 'card__content__referral' : 'card__content')
            const target = target_selector?target_selector[0]: null
            const gen_id = Math.floor(Date.now() /1000)
            toPng(target, {pixelRatio: 6}).then((dataUrl) => {
                this.loading = false
                    const link = document.createElement('a')
                    link.download = gen_id + '.' + 'png'
                    link.href = dataUrl
                    link.click()
                })
                .catch((err) => {
                    console.log(err)
                })
        },
        printCard() {
            // Find the card entry that matches the current active layout
            const cardEntries = document.querySelectorAll('.card__entry')
            let activeCardEntry = null
            
            // Find the visible card entry based on current layout
            cardEntries.forEach(entry => {
                const cardContent = entry.querySelector('.card__content')
                if (cardContent) {
                    const isVertical = cardContent.classList.contains('vertical')
                    const isHorizontal = cardContent.classList.contains('horizontal')
                    
                    // Match the current layout
                    if ((this.card_layout === 'vertical' && isVertical) || 
                        (this.card_layout === 'horizontal' && isHorizontal)) {
                        activeCardEntry = entry
                    }
                }
            })
            
            // Fallback: if no match found, try to find any visible card
            if (!activeCardEntry) {
                activeCardEntry = document.querySelector('.card__entry')
            }
            
            if (!activeCardEntry) {
                if (window.$toast) {
                    window.$toast.error('Card not found. Please ensure the card is displayed.')
                }
                return
            }

            // Add print class to the active card entry
            activeCardEntry.classList.add('print-card')
            
            // Add vertical class if it's a vertical layout
            if (this.card_layout === 'vertical') {
                activeCardEntry.classList.add('vertical')
            }
            
            // Trigger browser print dialog
            window.print()
            
            // Remove print class after print dialog closes
            setTimeout(() => {
                activeCardEntry.classList.remove('print-card', 'vertical')
            }, 1000)
        },
        setForm(elements){
            const that = this
            Object.keys(elements).forEach(function (element) {
                that.form[element] = elements[element]
            })
        },
        partitionArray(array, size){
            return array.map( (e,i) => (i % size === 0) ? array.slice(i, i + size) : null ).filter( (e) => e )
        },
        withPhotosCheck(){
            if(this.form.with_photos){
                this.file_accept = '.zip'
            }
        },
        backgroundImageInputChange(e){
            if(e.target.files.length && e.target.files[0]){
                const file = e.target.files[0]
                const validation = this.validateFileUpload(file, 'image')
                if (!validation.valid) {
                    e.target.value = '' // Clear the input
                    return
                }

                const self = this
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.addEventListener('load', function(){
                    self.form.background = reader.result
                    self.refreshed_user_at = (new Date()).valueOf()
                    if (window.$toast) {
                        window.$toast.success('Background image uploaded successfully')
                    }
                })
            }
        },
        backgroundImageInputRefChange(e){
            if(e.target.files.length && e.target.files[0]){
                const self = this
                const reader = new FileReader()
                reader.readAsDataURL( e.target.files[0] )
                reader.addEventListener('load', function(){
                    self.form.background_ref = reader.result
                    this.refreshed_user_at = (new Date()).valueOf()
                })
            }
        },
        companyLogoInputChange(e){
            if(e.target.files.length && e.target.files[0]){
                const file = e.target.files[0]
                const validation = this.validateFileUpload(file, 'image')
                if (!validation.valid) {
                    e.target.value = '' // Clear the input
                    return
                }

                const self = this
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.addEventListener('load', function(){
                    self.form.logo = reader.result
                    self.refreshed_user_at = (new Date()).valueOf()
                    if (window.$toast) {
                        window.$toast.success('Logo uploaded successfully')
                    }
                })
                // this.form.logo = URL.createObjectURL(e.target.files[0]);
                // this.form.processing = true
                // let formData = new FormData()
                //
                // formData.append('company_logo', e.target.files[0])
                // this.axios.post(this.route('upload.company_logo'), formData).then(( response ) => {
                //     if(response.data && response.data.company_logo){
                //         this.form.logo = response.data.company_logo
                //         this.form.processing = false
                //         this.refreshed_user_at = (new Date()).valueOf()
                //     }else{
                //         alert('something went wrong!')
                //         this.form.processing = false
                //     }
                // }).catch((error) => {
                //     this.form.processing = false
                //     console.log(error)
                // })
            }
        },
        enableCsvImport(boolean){
            let el = document.getElementById('cardInputs'), all = el.getElementsByTagName('input'), i
            for (i = 0; i < all.length; i++) {
                if(all[i].type.toLowerCase() === 'text'){
                    all[i].disabled = boolean
                    if(boolean){
                        all[i].parentElement.classList.add('opacity-30')
                    }else{
                        all[i].parentElement.classList.remove('opacity-30')
                    }
                }
            }
        },
        fileInputChange(e) {
            this.form.processing = true
            let data = new FormData()
            const fileSel = document.getElementById('file')
            if(fileSel.files.length && fileSel.files[0]){
                data.append('file', document.getElementById('file').files[0])
                this.axios.post(this.route('import.file'), data).then(( response ) => {
                    if(response.data && response.data.csv && response.data.csv.length){
                        this.setCard(response.data.csv)
                        this.generateCardDesign()
                        this.form.processing = false
                        this.step = 2
                    }else{
                        if (window.$toast) {
                            window.$toast.error('Something went wrong while importing the file. Please try again.')
                        }
                        this.form.processing = false
                    }
                }).catch((error) => {
                    this.form.processing = false
                    console.error('File upload error:', error)

                    let errorMessage = 'Failed to import file. Please try again.'
                    if (error.response) {
                        if (error.response.status === 413) {
                            errorMessage = 'File is too large. Please use a smaller file.'
                        } else if (error.response.status === 415) {
                            errorMessage = 'Unsupported file type. Please use CSV, XLSX, or TSV format.'
                        } else if (error.response.data && error.response.data.message) {
                            errorMessage = error.response.data.message
                        }
                    } else if (error.message) {
                        if (error.message.includes('Network')) {
                            errorMessage = 'Network error. Please check your connection and try again.'
                        }
                    }

                    if (window.$toast) {
                        window.$toast.error(errorMessage)
                    }
                })
            }
        },
        setCard(data){
            this.form.cards = [...data]
            const cardData = this.form.cards.map((c, ci) => {
                const qrText = c.name + ' \n' + c.profession + ' \n' + c.location + ' \n' + c.phone + ' \n' + c.email + ' \n' + c.website
                return {name: qrText, index: ci}
            })
            this.createQr(cardData)
        },
        createQr(cardData){
            this.axios.post(this.route('create.qr'), { data: cardData }).then((response) => {
                if( response.data && response.data.images && response.data.images.length ){
                    this.setQrImageToCards(response.data.images)
                }
            }).catch((error) => {
                console.log(error)
                this.form.processing = false
            })
        },
        fileBrowse() {
            if(this.form.school_name){
                this.$refs.file.click()
            }else{
                if (window.$toast) {
                    window.$toast.warning('Please input school name first!')
                }
            }
        },
    },
}
</script>

<style scoped>
@media print {
    @page {
        size: A4;
        margin: 0;
    }

    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    body {
        margin: 0;
        padding: 0;
        background: white !important;
    }

    body * {
        visibility: hidden;
    }

    .print-card,
    .print-card * {
        visibility: visible;
    }

    .print-card {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        margin: 0;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    /* Horizontal card dimensions (standard business card) */
    .print-card:not(.vertical) {
        width: 85mm;
        height: 55mm;
    }

    /* Vertical card dimensions (rotated business card) */
    .print-card.vertical {
        width: 55mm;
        height: 85mm;
    }

    .print-card .card__entry {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    .print-card .card__content {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        transform: scale(1) !important;
        box-shadow: none !important;
        page-break-inside: avoid;
        break-inside: avoid;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Specific sizing for vertical cards */
    .print-card.vertical .card__content.vertical {
        width: 55mm !important;
        height: 85mm !important;
    }

    /* Specific sizing for horizontal cards */
    .print-card:not(.vertical) .card__content.horizontal {
        width: 85mm !important;
        height: 55mm !important;
    }
}
</style>





